import { axiosRequest, dashboardApi } from '@/store/api/base/api.base';
import { dropDiagnoseRun, writeDiagnoseRun } from '@/store/api/begateway/begateway.diagnose.cache';
import type {
    TypeDiagnoseCheck,
    TypeDiagnoseCheckInput,
    TypeDiagnoseRun,
    TypeDiagnoseRunInput,
    TypeDiagnoseRunState,
} from '@/types/begateway/types.begateway.diagnose';

export type TypeDiagnoseRunQuery = { target: string; state: TypeDiagnoseRunState | 'all' };

const PAGE_SIZE = 20;

const CLIENT_MARGIN_MS = 15_000;

type TypeDiagnoseRunPage = { items: TypeDiagnoseRun[]; page: { hasMore: boolean; nextCursor: string | null } | null };

const runUrl = (id: string): string => `/begateway/diagnose/runs/${encodeURIComponent(id)}`;

export const begatewayDiagnoseApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        runDiagnose: build.mutation<TypeDiagnoseRun, TypeDiagnoseRunInput>({
            queryFn: async (run) => {
                const result = await axiosRequest(
                    { url: '/begateway/diagnose/runs', method: 'post', data: { run }, timeout: run.timeoutMs + CLIENT_MARGIN_MS },
                    { fallback: 'Prova non eseguita', timeoutMessage: 'La dashboard ha smesso di aspettare il gateway' },
                );
                return 'error' in result ? result : { data: result.data as TypeDiagnoseRun };
            },
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    if (data.stored) writeDiagnoseRun(dispatch, data);
                } catch {
                }
            },
        }),

        listDiagnoseRuns: build.infiniteQuery<TypeDiagnoseRunPage, TypeDiagnoseRunQuery, string | null>({
            infiniteQueryOptions: {
                initialPageParam: null,
                getNextPageParam: (last) => (last.page?.hasMore && last.page.nextCursor ? last.page.nextCursor : undefined),
            },
            query: ({ queryArg, pageParam }) => ({
                url: '/begateway/diagnose/runs',
                params: {
                    ...(queryArg.target ? { target: queryArg.target } : {}),
                    ...(queryArg.state !== 'all' ? { state: queryArg.state } : {}),
                    limit: PAGE_SIZE,
                    ...(pageParam ? { cursor: pageParam } : {}),
                },
            }),
            providesTags: ['GatewayDiagnose'],
        }),

        readDiagnoseRun: build.query<TypeDiagnoseRun, string>({
            query: (id) => ({ url: runUrl(id) }),
            providesTags: (_result, _error, id) => [{ type: 'GatewayDiagnose', id }],
        }),

        deleteDiagnoseRun: build.mutation<{ id: string }, string>({
            query: (id) => ({ url: runUrl(id), method: 'delete' }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    dropDiagnoseRun(dispatch, id);
                } catch {
                }
            },
        }),

        checkDiagnose: build.mutation<TypeDiagnoseCheck, TypeDiagnoseCheckInput>({
            query: (check) => ({ url: '/begateway/diagnose/check', method: 'post', data: { check } }),
        }),
    }),
});

export const {
    useRunDiagnoseMutation, useListDiagnoseRunsInfiniteQuery, useReadDiagnoseRunQuery, useDeleteDiagnoseRunMutation, useCheckDiagnoseMutation,
} = begatewayDiagnoseApi;
